
import React, { useState, useEffect, useRef } from 'react';
import {
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  VideoCameraIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  CommandLineIcon,
  CircleStackIcon,
  CpuChipIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  TrophyIcon as TrophyIconSolid,
} from "@heroicons/react/24/outline";
import API_BASE_URL from "../Config/config";
import EvidenceSubmission from './EvidenceSubmission';
import { useErrorHandler } from '../error-framework/ErrorContext';
import { normalizeError } from '../error-framework/errorMapper';

/**
 * AI Mentor Controller - AI-First Architecture
 *
 * The AI Mentor is now the ONLY interface for:
 * - Task introduction and guidance
 * - Automatic explanation and video offers (AI-driven, not manual)
 * - Evidence collection (AI determines what's needed)
 * - Code execution via built-in compiler
 * - GitHub integration (shown only when AI determines it's needed)
 * - Adaptive mentoring based on learner behavior
 */
const AIMentorController = ({ task, enrollmentId, experienceLevel, userId, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evidenceType, setEvidenceType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showExplanationOffer, setShowExplanationOffer] = useState(false);
  const [showVideoOffer, setShowVideoOffer] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [compilerOpen, setCompilerOpen] = useState(false);
  const [code, setCode] = useState('');
  const [codeOutput, setCodeOutput] = useState(null);
  const [evidenceData, setEvidenceData] = useState({});
  const [githubInfo, setGithubInfo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('ai_mentor_lang') || 'English');
  const [activeSubTab, setActiveSubTab] = useState('architecture');
  const [activeMainTab, setActiveMainTab] = useState('mentor'); // 'mentor', 'verify', 'feedback'
  const scrollRef = useRef(null);
  const { reportError } = useErrorHandler();

  const languages = [
    'English', 'Telugu', 'Hindi', 'Tamil', 'Kannada', 
    'Malayalam', 'Marathi', 'Bengali', 'Gujarati', 'Punjabi'
  ];

  const sections = [
    { id: 'architecture', label: 'Architecture' },
    { id: 'folder_structure', label: 'Folder Structure' },
    { id: 'beginner', label: 'Beginner' },
    { id: 'intermediate', label: 'Intermediate' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'subtopics', label: 'Subtopics' },
    { id: 'usage', label: 'Real World Usage' },
    { id: 'best_practices', label: 'Best Practices' },
    { id: 'mistakes', label: 'Common Mistakes' },
    { id: 'interview', label: 'Interview Questions' },
    { id: 'path', label: 'Learning Path' },
    { id: 'summary', label: 'Summary' },
    { id: 'revision', label: 'Quick Revision' },
  ];

  const [structuredContent, setStructuredContent] = useState({});

  useEffect(() => {
    localStorage.setItem('ai_mentor_lang', selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    if (task) {
      initTask();
      fetchGithubInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.task_id, selectedLanguage]);

  const fetchGithubInfo = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { "user-id": userId };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/bootcamp/${enrollmentId}/github-info`, {
        headers
      });
      const data = await res.json();
      if (data.ok) {
        setGithubInfo(data.github || data.enrollment);
      }
    } catch (err) {
      console.error("Failed to fetch GitHub info:", err);
    }
  };

  const initTask = async () => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json", "user-id": userId };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/ai/generate-mentor-content`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          userId,
          enrollmentId,
          taskId: task.id || task.task_id,
          title: task.title || task.name,
          description: task.description,
          useCase: task.useCase || task.use_case,
          expectedOutput: task.expectedOutput || task.expected_output,
          keyTopics: task.keyTopics || task.key_topics || task.subtopics,
          language: selectedLanguage,
          experienceLevel
        }),
      });
      const data = await res.json();
      console.log('AI Mentor API Response:', { status: res.status, data });

      if (!res.ok) {
        throw new Error(`API returned ${res.status}: ${data.error || data.message || 'Unknown error'}`);
      }

      if (data.ok && data.data) {
        setMessages([{
          role: 'assistant',
          content: data.data.explanation || data.data,
          type: 'intro'
        }]);

        // Use structured content from endpoint
        if (data.data.structuredContent) {
          setStructuredContent(data.data.structuredContent);
        } else {
          // Fallback: simple parser for markdown sections
          const sectionsData = {};
          const content = data.data.explanation || data.data;
          sections.forEach(sec => {
            const regex = new RegExp(`## \\d*\\.?\\s*${sec.label}([\\s\\S]*?)(?=##|$)`, 'i');
            const match = content.match(regex);
            if (match) {
              sectionsData[sec.id] = match[1].trim();
            }
          });
          setStructuredContent(sectionsData);
        }

        setEvidenceType(data.data.requiredEvidence);
        if (data.data.videoUrl) {
          setVideoUrl(data.data.videoUrl);
        }
      }
    } catch (err) {
      console.error("Failed to init AI Mentor:", err);
      reportError(err, { feature: 'bootcamp' }, initTask);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionType) => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json", "user-id": userId };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/resources`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          userId,
          enrollmentId,
          taskId: task.id || task.task_id,
          experienceLevel,
          task,
          language: selectedLanguage,
          context: actionType
        }),
      });
      const data = await res.json();
      if (data.ok) {
        if (actionType === 'video') {
          if (data.data.videoUrl) {
            setVideoUrl(data.data.videoUrl);
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: "🎥 Here is your personalized AI Video explanation.",
              type: 'video'
            }]);
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.data.explanation || "Video is being generated. Please check status in a moment.",
              type: 'video_pending'
            }]);
          }
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: data.data.explanation,
            type: 'explanation'
          }]);
        }
      }
    } catch (err) {
      console.error("AI Action failed:", err);
      reportError(err, { feature: 'bootcamp' }, () => handleAction(actionType));
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (!githubInfo) return "No repository provisioned";
    const status = githubInfo.provisioning_status || 'PENDING';
    
    switch(status) {
      case 'PENDING': return 'Initializing...';
      case 'VALIDATING_GITHUB': return 'Validating GitHub account...';
      case 'CREATING_REPOSITORY': return 'Creating repository...';
      case 'PUSHING_TEMPLATE': return 'Pushing starter template...';
      case 'ADDING_COLLABORATOR': return 'Adding you as collaborator...';
      case 'INVITATION_SENT': return 'Invitation sent. Check your email!';
      case 'READY': return 'Repository ready';
      case 'FAILED': return 'Provisioning failed. Retrying...';
      default: return status;
    }
  };

  const handleRunCode = async () => {
    if (!code.trim()) {
      setCodeOutput({ error: 'Please write some code first.' });
      return;
    }

    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json", "user-id": userId };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const language = evidenceType?.language || task.language || 'python';
      const res = await fetch(`${API_BASE_URL}/api/compiler/execute`, {
        method: "POST",
        headers,
        body: JSON.stringify({ language, code, input: '' }),
      });
      const data = await res.json();
      setCodeOutput(data);

      // Store code in evidence
      setEvidenceData(prev => ({ ...prev, code, language }));
    } catch (err) {
      setCodeOutput({ error: 'Compiler service unavailable' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (field, file) => {
    // Handle screenshot/diagram uploads
    const reader = new FileReader();
    reader.onloadend = () => {
      setEvidenceData(prev => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleTextInput = (field, value) => {
    setEvidenceData(prev => ({ ...prev, [field]: value }));
  };

  const handleEvidenceChange = (field, value) => {
    setEvidenceData(prev => ({ ...prev, [field]: value }));
  };

  const handleVerify = async () => {
    setSubmitting(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json", "user-id": userId };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/bootcamp/verify`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          enrollmentId,
          taskId: task.id || task.task_id,
          taskMetadata: task,
          evidence: evidenceData,
          userId
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Verification service unavailable. Please try again later.');
      }

      if (!data.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      const feedbackData = data.feedback || data;

      setFeedback(feedbackData);
      setActiveMainTab('feedback');

      if (feedbackData.status === 'PASS') {
        const passMsg = `🎉 Congratulations! You passed with a score of ${feedbackData.score}/100.\n\n${feedbackData.industry_best_practice || ''}\n\n**Strengths:**\n${(feedbackData.strengths || []).map(s => `• ${s}`).join('\n')}`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: passMsg,
          type: 'success'
        }]);
        if (onComplete) onComplete(feedbackData);
      } else {
        setFailureCount(prev => prev + 1);

        let retryMessage = `I've reviewed your work. Score: ${feedbackData.score}/100\n\n`;
        if (feedbackData.improvement_areas) {
          retryMessage += `**Areas to improve:**\n${(feedbackData.improvement_areas || []).map(w => `• ${w}`).join('\n')}\n\n`;
        } else {
          retryMessage += `**Areas to improve:**\n${(feedbackData.weaknesses || []).map(w => `• ${w}`).join('\n')}\n\n`;
        }
        retryMessage += feedbackData.recommended_revision || feedbackData.industry_best_practice || '';

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: retryMessage,
          type: 'retry'
        }]);
      }
    } catch (err) {
      console.error("Verification failed:", err);
      const normalized = normalizeError(err, { feature: 'bootcamp' });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: normalized.userMessage,
        type: 'error'
      }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-bgMain rounded-2xl border border-secondary/20 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-secondary/20 bg-secondary/5 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></div>
            <h3 className="font-bold text-primary flex items-center gap-2">
              <SparklesIcon className="h-5 w-5" />
              AI Mentor
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="text-[10px] font-bold bg-secondary/10 px-2 py-1 rounded border border-secondary/20 text-gray-300 cursor-pointer hover:bg-secondary/20"
            >
              {languages.map(lang => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
            <div className="text-[10px] text-muted font-bold bg-secondary/10 px-2 py-1 rounded border border-secondary/20 uppercase">
              {experienceLevel} LEVEL
            </div>
            {githubInfo && (
              <div className={`text-[10px] font-bold px-2 py-1 rounded border ${
                githubInfo.provisioning_status === 'READY'
                  ? 'bg-green-500/10 text-green-500 border-green-500/20'
                  : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
              }`}>
                {getStatusDisplay().toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveMainTab('mentor')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeMainTab === 'mentor' 
                ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                : 'bg-secondary/10 text-muted hover:bg-secondary/20'
            }`}
          >
            AI Mentor
          </button>
          <button
            onClick={() => setActiveMainTab('verify')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeMainTab === 'verify' 
                ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                : 'bg-secondary/10 text-muted hover:bg-secondary/20'
            }`}
          >
            Verify My Work
          </button>
          <button
            onClick={() => setActiveMainTab('feedback')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeMainTab === 'feedback' 
                ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                : 'bg-secondary/10 text-muted hover:bg-secondary/20'
            }`}
          >
            Feedback
          </button>
        </div>
      </div>

      {activeMainTab === 'mentor' && (
        <>
          {/* Subtabs for Mentor Content */}
          <div className="px-4 py-2 border-b border-secondary/10 bg-secondary/5 overflow-x-auto scrollbar-none flex items-center gap-2">
            {sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => setActiveSubTab(sec.id)}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-[10px] font-bold transition-all border ${
                  activeSubTab === sec.id
                    ? 'bg-primary/20 border-primary/50 text-primary'
                    : 'bg-transparent border-secondary/20 text-muted hover:border-primary/30'
                }`}
              >
                {sec.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-thumb-primary/20">
            {loading && Object.keys(structuredContent).length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted">AI is generating your guidance...</p>
              </div>
            ) : (
              <div className="animate-fadeIn">
                <div className="prose prose-invert prose-sm max-w-none">
                  {structuredContent[activeSubTab] ? (
                    <div className="whitespace-pre-line text-gray-200">
                      {structuredContent[activeSubTab]}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <ExclamationTriangleIcon className="h-10 w-10 text-muted mb-4" />
                      <p className="text-muted">No content available for this section yet.</p>
                      <button 
                        onClick={initTask}
                        className="mt-4 text-xs font-bold text-primary hover:underline"
                      >
                        Regenerate Guidance
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Audio & Video Controls */}
            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-secondary/10">
              <button className="flex items-center gap-2 px-4 py-2 bg-secondary/10 hover:bg-secondary/20 rounded-xl text-xs font-bold text-gray-300 transition-all">
                <SpeakerWaveIcon className="h-4 w-4 text-primary" />
                Listen to Audio
              </button>
              {videoUrl && (
                <button 
                  onClick={() => handleAction('video')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 rounded-xl text-xs font-bold text-primary transition-all border border-primary/20"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  Watch Video
                </button>
              )}
              <button 
                onClick={initTask}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-secondary/10 hover:bg-secondary/20 rounded-xl text-xs font-bold text-gray-300 transition-all"
              >
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Regenerate Guidance
              </button>
            </div>
          </div>
        </>
      )}

      {activeMainTab === 'verify' && (
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-primary/20">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-primary/5 rounded-2xl border border-primary/20 p-6">
              <h4 className="text-lg font-bold text-primary mb-2">Verify My Work</h4>
              <p className="text-sm text-muted mb-6">
                Submit your evidence for review. AI will evaluate your work based on the expected output.
              </p>
              
              {evidenceType ? (
                <div className="space-y-6">
                  <EvidenceSubmission
                    evidenceType={evidenceType}
                    onEvidenceChange={handleEvidenceChange}
                    githubInfo={githubInfo}
                    onRefreshGitHub={fetchGithubInfo}
                    userId={userId}
                    enrollmentId={enrollmentId}
                    taskId={task?.id || task?.task_id}
                  />
                  
                  <button
                    onClick={handleVerify}
                    disabled={submitting || loading}
                    className="w-full py-4 bg-primary text-white rounded-2xl font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 shadow-xl shadow-primary/20 transition-all transform hover:scale-[1.02]"
                  >
                    {submitting ? (
                      <ArrowPathIcon className="h-6 w-6 animate-spin" />
                    ) : (
                      <ShieldCheckIcon className="h-6 w-6" />
                    )}
                    Submit For Verification
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <ArrowPathIcon className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeMainTab === 'feedback' && (
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-primary/20">
          {feedback ? (
            <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
              {/* Score Card */}
              <div className={`rounded-2xl border p-6 flex items-center justify-between ${
                feedback.status === 'PASS' 
                  ? 'bg-green-500/5 border-green-500/20' 
                  : 'bg-red-500/5 border-red-500/20'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold border-4 ${
                    feedback.status === 'PASS' 
                      ? 'border-green-500 text-green-500' 
                      : 'border-red-500 text-red-500'
                  }`}>
                    {feedback.score}
                  </div>
                  <div>
                    <h4 className={`text-xl font-bold ${
                      feedback.status === 'PASS' ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {feedback.status === 'PASS' ? 'PASSED' : 'RETRY NEEDED'}
                    </h4>
                    <p className="text-sm text-muted">
                      {feedback.status === 'PASS' 
                        ? '🎉 Congratulations! You have successfully completed this task.' 
                        : 'Please review the feedback and resubmit your work.'}
                    </p>
                  </div>
                </div>
                {feedback.status === 'PASS' && (
                  <div className="flex flex-col items-center px-4 py-2 bg-green-500/20 rounded-xl">
                    <TrophyIconSolid className="h-6 w-6 text-yellow-500" />
                    <span className="text-[10px] font-bold text-green-500 mt-1">TASK UNLOCKED</span>
                  </div>
                )}
              </div>

              {/* Detailed Feedback */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-secondary/10 rounded-2xl border border-secondary/20 p-6">
                  <h5 className="font-bold text-gray-200 mb-4 flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    Strengths
                  </h5>
                  <ul className="space-y-3">
                    {(feedback.strengths || []).map((s, i) => (
                      <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                        <span className="text-green-500 mt-1">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-secondary/10 rounded-2xl border border-secondary/20 p-6">
                  <h5 className="font-bold text-gray-200 mb-4 flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                    Areas for Improvement
                  </h5>
                  <ul className="space-y-3">
                    {(feedback.improvement_areas || feedback.weaknesses || []).map((w, i) => (
                      <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Recommendations & Industry Best Practices */}
              <div className="bg-primary/5 rounded-2xl border border-primary/20 p-6">
                <h5 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <CommandLineIcon className="h-5 w-5" />
                  AI Mentor Recommendations
                </h5>
                <div className="prose prose-invert prose-sm max-w-none text-gray-300 whitespace-pre-line">
                  {feedback.industry_best_practice || feedback.recommended_revision}
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-secondary/5 rounded-2xl border border-secondary/20 p-6">
                <h5 className="font-bold text-gray-200 mb-2">Next Steps</h5>
                <p className="text-sm text-muted">
                  {feedback.next_learning_recommendation || "Continue with the next task in your roadmap to maintain your learning streak!"}
                </p>
                {feedback.status === 'RETRY' && (
                  <button
                    onClick={() => setActiveMainTab('verify')}
                    className="mt-4 px-6 py-2 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary/90 transition-all"
                  >
                    Resubmit Work
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ChatBubbleLeftRightIcon className="h-16 w-16 text-muted mb-4" />
              <h4 className="text-lg font-bold text-gray-300">No Feedback Yet</h4>
              <p className="text-sm text-muted max-w-xs mt-2">
                Submit your work in the "Verify My Work" tab to receive detailed AI feedback.
              </p>
              <button
                onClick={() => setActiveMainTab('verify')}
                className="mt-6 px-6 py-2 bg-primary text-white rounded-xl text-xs font-bold"
              >
                Go to Verification
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIMentorController;
